import React, { useEffect, useState } from "react";
import LoadingIndicator from '../shared/LoadingIndicator';
import { Tooltip } from "@blueprintjs/core";
import axios from "axios";
import Cookies from "js-cookie";
import { FilledButton } from "../buttons-20250224T111359Z-001/buttons";
import Utilities from "../../utils/Utilities";

const STYLES = `
.performance-wrapper {
width: 60%;
}

.review-card {
background: #ffffff;
border: 1px solid #e6e8eb;
border-radius: 6px;
padding: 20px 24px;
margin-bottom: 18px;
display: flex;
gap: 30px; 
align-items: flex-start;
}

/* LEFT COLUMN */
.review-left {
flex: 1 1 auto;
min-width: 0;
}

/* TITLE */
.review-title {
font-size: 15px;
font-weight: 600;
max-width: 350px;
color: #2e2e2e;
margin-bottom: 4px;

white-space: nowrap;
overflow: hidden;
text-overflow: ellipsis;
}

/* DATE */
.review-date {
font-size: 13px;
color: #8b8b8b;
}

/* MIDDLE COLUMN */
.review-rating {
flex: 0 0 180px;
display: flex;
flex-direction: column;
}

.rating-label {
font-size: 13px;
color: #8b8b8b;
}

.rating-row {
margin-top: 6px;
display: flex;
align-items: center;
}

.rating-value {
font-size: 14px;
font-weight: 600;
color: #2e2e2e;
margin-right: 8px;
}

.rating-badge {
background-color: #e6f4ea;
color: #2e7d32;
padding: 3px 10px;
border-radius: 20px;
font-size: 12px;
font-weight: 500;
}

.not-started-badge {
background-color: #f1f3f5;
color: #6c757d;
padding: 3px 10px;
border-radius: 20px;
font-size: 12px;
font-weight: 500;
}
/* RIGHT COLUMN */
.review-action {
display: flex;
justify-content: flex-end;
}

.view-btn {
border: 1px solid #2962ff;
color: #2962ff;
background: #fff;
border-radius: 4px;
padding: 6px 14px;
font-size: 13px;
cursor: pointer;
transition: all 0.2s ease;
}

.view-btn:hover {
background: #2962ff;
color: #fff;
}
.rating-container {
display: flex;
flex-direction: column;
align-items: flex-start;
}

.rating-row {
display: flex;
align-items: center;
margin-top: 4px;
}

.not-started-badge {
background-color: #f1f3f5;
color: #6c757d;
padding: 3px 10px;
border-radius: 20px;
font-size: 12px;
font-weight: 500;
}
`;

const UserProfilePerformanceTimeline = ({
    userId,
    userName = "",
    userEmail = "",
    isCurrentUser = true,
    currentUserId
}) => {
    const [timelineData, setTimelineData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openingReviewId, setOpeningReviewId] = useState(null);

    const formatMonthYear = (dateStr) => {
        if (!dateStr) return "";

        const date = new Date(dateStr);

        return date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });
    };

    const fetchUserPerformanceTimeline = async () => {
        const baseUrl = Cookies.get("apiEnv");
        if (!baseUrl) return;

        setIsLoading(true);

        try {
            const params = {};
            if (userId) {
                params.user_id = userId;
            }
            const response = await axios.get(
                `${baseUrl}/review_workflows/user_performance_data`,
                { params }
            );

            const rawData = Array.isArray(response.data?.data)
                ? response.data.data
                : [];

            const transformed = rawData
                .sort((a, b) => new Date(b?.end_time) - new Date(a?.end_time))
                .map((item) => ({
                    id: item.id,
                    title: item.title,
                    timeline:
                        item?.start_time && item?.end_time
                            ? `${formatMonthYear(item.start_time)} - ${formatMonthYear(item.end_time)}`
                            : item?.end_time
                                ? formatMonthYear(item.end_time)
                                : "",
                    rating:
                        item?.rating !== null && item?.rating !== undefined
                            ? Number(item.rating).toFixed(2)
                            : null,
                    rating_label: item?.rating_label,
                }));

            setTimelineData(transformed);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const resolveManagerIdForCycle = async (goalSettingId) => {
        const baseUrl = Cookies.get("apiEnv");
        if (!baseUrl || !goalSettingId || !userId) return null;

        const search = (userEmail || userName || "").trim();

        const params = {
            goal_setting_id: goalSettingId,
            page: 1,
            per_page: 200,
            ...(search ? { search } : {})
        };

        try {
            const res = await axios.get(`${baseUrl}/review_workflows`, { params });
            const rows = Array.isArray(res?.data?.data) ? res.data.data : [];

            const match = rows.find((item) => {
                const rowUserId = item?.user?.id || item?.user_id || item?.id;
                return String(rowUserId) === String(userId);
            });

            return match?.manager_id || null;
        } catch (e) {
            console.error("Failed to resolve manager_id for review workflow", e);
            return null;
        }
    };

    const handleReview = async (review) => {
        if (isCurrentUser) {
            const encoded = btoa(review.id);
            window.location.href = `/performance/myassessment?cycle=${encoded}`;
            return;
        }
        setOpeningReviewId(review?.id || null);
        try {
            const managerId = await resolveManagerIdForCycle(review?.id);
            const payload = {
                u: userId,
                g: review.id,
                m: managerId || currentUserId,
                n: userName || ""
            };

            sessionStorage.setItem("team_review_context", JSON.stringify(payload));
            const encoded = btoa(JSON.stringify(payload));
            Utilities.navigate(`/performance/team/review?r=${encodeURIComponent(encoded)}`);
        } finally {
            setOpeningReviewId(null);
        }
    };

    useEffect(() => {
        fetchUserPerformanceTimeline();
    }, [userId, isCurrentUser]);

    return (
        <div className="page-bg">
            <style>{STYLES}</style>

            <div className="performance-wrapper">
                <h3 style={{ marginBottom: 20, fontWeight: 600 }}>
                    {isCurrentUser ? "My Performance Reviews" : "Performance Reviews"}
                </h3>

                {isLoading ? (
                    <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "40px"
                    }}>
                        <LoadingIndicator size={30} />
                    </div>
                ) : timelineData.length === 0 ? (
                    <div style={{
                        textAlign: "center",
                        marginTop: "40px",
                        color: "#6B7280"
                    }}>
                        No performance reviews found
                    </div>
                ) : (
                    timelineData?.map((review) => (

                        <div key={review.id} className="review-card">
                            {/* LEFT */}
                            <div className="review-left">
                                <Tooltip content={review.title} position="top">
                                    <div className="review-title">
                                        {review.title || ""}
                                    </div>
                                </Tooltip>

                                <div className="review-date">
                                    {review.timeline || ""}
                                </div>
                            </div>

                            {/* MIDDLE */}
                            <div className="review-rating">
                                <span className="rating-label">
                                    Performance Rating
                                </span>
                                <div className="rating-row">
                                    {review.rating !== null ? (
                                        <>
                                            <span className="rating-value">
                                                {review.rating}
                                            </span>

                                            {review.rating_label && (
                                                <span className="rating-badge">
                                                    {review.rating_label}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <span className="not-started-badge">
                                            Not Started
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT */}
                            <div className="review-action">
                                <FilledButton
                                    text={isCurrentUser ? "View Review" : "View Details"}
                                    onClick={() => handleReview(review)}
                                    disabled={openingReviewId === review.id}
                                />
                            </div>
                        </div>
                    ))
                )}
            </div >
        </div >
    );
};

export default UserProfilePerformanceTimeline;
